import React from 'react';
import PropTypes from 'prop-types';
import { List, Map } from 'immutable';
import { cardImageUrl } from '../services/helpers';
import { EnergySymbol } from '../Energy';
import { energySymbols } from '../Energy/energySymbols';
import { Art, Count, Damage, Energies, Face, StatusBadges, Trainers } from './styled';
import { AttachedTrainer, ConditionMarker } from './Markers';
import { conditionDisplay } from './conditionDisplay';

/**
 * A card as it shows on the board or in a hand. With no card (or a face-down one) it shows the card back.
 */
const CardFace = ({
  card, label, damage, conditions, protection, hindrance, hinderedBy, trainers, conversions, energyAttachments, count,
  targetable, dim, selected,
}) => {
  const symbols = energySymbols(energyAttachments);
  // Asleep, Paralyzed and Confused turn the card; poison and burn put a marker on it; anything else is in words.
  const shown = conditionDisplay(conditions ? conditions.toArray() : []);
  const hasConditions = shown.words.length > 0;
  const hasTrainers = trainers && trainers.size > 0;
  const hasConversions = conversions && conversions.size > 0;

  return (
    <Face
      // A turned card turns everything on it, the way real attached cards turn with the Pokémon; the badges you read
      // (damage, markers, words) turn back upright (--upright).
      style={shown.turn ? { transform: `rotate(${shown.turn}deg)`, '--upright': `rotate(${-shown.turn}deg)` } : undefined}
      data-turn={shown.turn || undefined}
      $targetable={targetable}
      data-targetable={targetable ? 'true' : undefined}
      $dim={dim}
      $selected={selected}
      role="img"
      aria-label={[label || (card && card.get('name')) || 'Face-down card', ...(conditions ? conditions.toArray() : [])].join(', ')}
    >
      <Art style={{ backgroundImage: `url(${cardImageUrl(card)})` }} />
      {shown.markers.map(kind => <ConditionMarker key={kind} kind={kind} />)}
      {damage > 0 && <Damage>{damage}</Damage>}
      {(hasConditions || protection || hinderedBy || hasConversions) && <StatusBadges>
        {hasConditions && shown.words.map(name => <li key={name}>{name}</li>)}
        {protection && <li data-kind="protection" title={protection} aria-label={protection}>Protected</li>}
        {hinderedBy && <li data-kind="hindrance" title={hindrance} aria-label={`${hinderedBy}: ${hindrance}`}>{hinderedBy}</li>}
        {hasConversions && conversions.map(text => <li key={text} data-kind="conversion">{text}</li>)}
      </StatusBadges>}
      {symbols.size > 0 && <Energies>
        {/* One group per energy card (a Double Colorless shows two), so a card flying in can hide its own. */}
        {energyAttachments.map((attachment, i) => (
          <span key={attachment.get('id') || i} data-energy-attachment style={{ display: 'contents' }}>
            {energySymbols(List([attachment])).map((type, j) => <EnergySymbol key={j} type={type} size="var(--energy-size)" />)}
          </span>
        ))}
      </Energies>}
      {hasTrainers && <Trainers>{trainers.map((trainer, i) => <AttachedTrainer key={i} card={trainer} />)}</Trainers>}
      {count !== undefined && count !== null && <Count>{count}</Count>}
    </Face>
  );
};

CardFace.propTypes = {
  card: PropTypes.instanceOf(Map),
  /** What a screen reader calls the card, when its name is not enough (a face-down prize). */
  label: PropTypes.string,
  damage: PropTypes.number,
  /** Special conditions by name, like Asleep or Poisoned; only an active Pokémon has them (conditionDisplay). */
  conditions: PropTypes.instanceOf(List),
  /** Protection from attacks this turn, like Withdraw's, as the server describes it ("Protected from damage"). */
  protection: PropTypes.string,
  /** What an opponent's attack like Amnesia stops this turn ("Can't use Ember"), and that attack's name. */
  hindrance: PropTypes.string,
  hinderedBy: PropTypes.string,
  /** Trainers attached to the Pokémon, like PlusPower or Defender, as catalog cards; one icon each, as they stack. */
  trainers: PropTypes.instanceOf(List),
  /** A Weakness or Resistance an attack changed (Conversion), like "Weak to Fighting". */
  conversions: PropTypes.instanceOf(List),
  energyAttachments: PropTypes.instanceOf(List),
  count: PropTypes.number,
  targetable: PropTypes.bool,
  dim: PropTypes.bool,
  selected: PropTypes.bool,
};

export default CardFace;
